(() => {
  if (window.PuffTimeShowcaseLoader) return;

  const selector = '[data-puff-time-showcase], [data-puff-time-experience]';
  const contracts = Object.freeze({
    showcase: Object.freeze({
      mount: 'mountPuffTimeShowcase',
      unmount: 'unmountPuffTimeShowcase',
    }),
    'try-to-roll': Object.freeze({
      mount: 'mountPuffTimeTryToRoll',
      unmount: 'unmountPuffTimeTryToRoll',
    }),
    gallery: Object.freeze({
      mount: 'mountPuffTimeGallery',
      unmount: 'unmountPuffTimeGallery',
    }),
    worlds: Object.freeze({
      mount: 'mountPuffTimeWorlds',
      unmount: 'unmountPuffTimeWorlds',
    }),
  });
  const records = new WeakMap();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(handleIntersection, { rootMargin: '240px 0px' })
    : null;

  function observe(root = document) {
    getHosts(root).forEach((host) => {
      if (records.has(host)) return;
      host.dataset.showcaseState = 'observed';
      if (observer) observer.observe(host);
      else void mount(host);
    });
  }

  async function mount(host) {
    if (records.has(host)) return records.get(host).promise;
    const viewer = host.querySelector('[data-puff-time-viewer]');
    const moduleUrl = safeModuleUrl(host.dataset.moduleUrl);
    const contract = contracts[host.dataset.experienceContract || 'showcase'];
    if (!viewer || !moduleUrl || !contract) {
      host.dataset.showcaseState = 'fallback';
      return null;
    }

    const record = { controller: null, disposed: false, module: null, promise: null };
    record.promise = (async () => {
      try {
        host.dataset.showcaseState = 'loading';
        const experience = await import(moduleUrl);
        record.module = experience;
        if (record.disposed) return null;
        if (typeof experience[contract.mount] !== 'function') {
          throw new TypeError('Invalid PUFF TIME experience module.');
        }
        record.controller = await experience[contract.mount](viewer, readConfig(host));
        if (record.disposed) {
          record.controller?.unmount();
          return null;
        }
        host.dataset.showcaseState = 'ready';
        return record.controller;
      } catch (error) {
        host.dataset.showcaseState = 'fallback';
        console.error('PUFF TIME showcase loader failed', error);
        return null;
      }
    })();
    records.set(host, record);
    return record.promise;
  }

  function unmount(host) {
    observer?.unobserve(host);
    const record = records.get(host);
    if (!record) return;
    record.disposed = true;
    record.controller?.unmount();
    const viewer = host.querySelector('[data-puff-time-viewer]');
    const contract = contracts[host.dataset.experienceContract || 'showcase'];
    if (viewer && contract && typeof record.module?.[contract.unmount] === 'function') {
      record.module[contract.unmount](viewer);
    }
    records.delete(host);
  }

  async function toggleSettings(force) {
    const host = getHosts(document).find((candidate) => candidate.getClientRects().length > 0);
    if (!host) return false;
    const controller = await mount(host);
    return controller?.toggleSettings(force) ?? false;
  }

  function handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      void mount(entry.target);
    });
  }

  function getHosts(root) {
    const hosts = [];
    if (root.matches?.(selector)) hosts.push(root);
    root.querySelectorAll?.(selector).forEach((host) => hosts.push(host));
    return hosts;
  }

  function readConfig(host) {
    try {
      return JSON.parse(host.querySelector('[data-puff-time-config]')?.textContent || '{}');
    } catch (error) {
      console.error('Invalid PUFF TIME showcase configuration', error);
      return {};
    }
  }

  function safeModuleUrl(value) {
    try {
      const url = new URL(value, window.location.href);
      const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);
      if (url.protocol !== 'https:' && !localHttp) return '';
      if (url.username || url.password) return '';
      return url.href;
    } catch {
      return '';
    }
  }

  document.addEventListener('shopify:section:load', (event) => observe(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    getHosts(event.target).forEach(unmount);
  });

  window.PuffTimeShowcaseLoader = Object.freeze({ observe, mount, unmount, toggleSettings });
  observe();
})();
